import { Button } from "@/components/ui/button";
import { SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PanelRightClose } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { useState } from "react";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import remarkGfm from "remark-gfm";
import { CopyIcon, FileTextIcon, ScrollText, Check } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTypingEffect } from "@/hooks/useTypingEffect";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEffect, useRef } from "react";

interface ReferencePanelProps {
  reference: Record<string, unknown>;
  title: string;
  content: string;
  onCopy?: () => void;
}

export function ReferencePanel({ reference, title, content, onCopy }: ReferencePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isPhone = useMediaQuery("(max-width: 576px)");
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);

  const handleSummarize = async () => {
    setIsLoading(true);
    setSummary("Ordinal is summarizing the reference...");
    try {
      const response = await fetch("/api/v1/completions/summarize_citation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          citation_summary: {
            title: title,
            text: `Title: ${title} \n Text: ${content}`,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch summary");
      }

      const data = await response.json();
      setSummary(data.response);
    } catch (error) {
      console.error("Error getting summary:", error);
      Honeybadger.notify(error);
    } finally {
      setIsLoading(false);
    }
  };

  const typedSummary = useTypingEffect(summary || "");


  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (summary && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [summary]);

  const contentElement = (
    <ScrollArea className="h-[calc(100vh-8rem)]">
      <div className="px-4">
        <div className="prose dark:prose-invert">
          <h3>{title}</h3>
          <SafeMarkdown remarkPlugins={[remarkGfm]}>{content}</SafeMarkdown>
        </div>
        {summary && (
          <div className="mt-4 p-4 prose dark:prose-invert rounded-lg">
            <h3 className="font-semibold mb-2">Summary:</h3>
            <SafeMarkdown remarkPlugins={[remarkGfm]}>
              {typedSummary}
            </SafeMarkdown>
          </div>
        )}
      </div>
      <span ref={contentRef}></span>
    </ScrollArea>
  );

  if (isPhone) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 opacity-75 hover:opacity-100 focus:opacity-100"
          >
            <ScrollText className="h-5 w-5" />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center justify-center rounded-full bg-green-500 h-4 w-4">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Verified</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <span className="sr-only">Toggle reference panel</span>
          </button>
        </DrawerTrigger>
        <DrawerContent className="max-h-svh">
          <DrawerHeader>
            <DrawerTitle>Reference</DrawerTitle>
          </DrawerHeader>
          {contentElement}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <SheetContent className="w-full sm:max-w-3xl">
      <SheetHeader>
        <SheetTitle>{title}</SheetTitle>
      </SheetHeader>
      {contentElement}
      <div className="sticky bottom-0 mt-4 flex gap-4 bg-background p-4 border-t">
        {onCopy && (
          <button onClick={onCopy} className="flex items-center gap-2 text-sm opacity-75 hover:opacity-100">
            <CopyIcon className="h-4 w-4" />
            Copy content
          </button>
        )}
        <button
          onClick={handleSummarize}
          disabled={isLoading}
          className="flex items-center gap-2 text-sm opacity-75 hover:opacity-100 disabled:opacity-50"
        >
          <FileTextIcon className="h-4 w-4" />
          {isLoading ? "Summarizing..." : "Summarize"}
        </button>
      </div>
    </SheetContent>
  );
}

